import { forwardRef, InputHTMLAttributes, useRef, useState } from 'react';

import { inputButtonStyle, inputContainerRecipe, inputStyle, relativeWrapperStyle } from './InputPassword.css';
import { useInputIds } from '../../hooks/useInputIds';
import { useValidationErrorEvent } from '../../hooks/useValidationErrorEvent';
import { VisibilityIcon, VisibilityOffIcon } from '../../icons';
import { useI18nTranslations } from '../../providers/i18n';
import { mergeRefs } from '../../util';
import { Box } from '../Box/Box';
import { InputBaseProps } from '../Input/Input';
import { InputError } from '../Input/InputError';
import { Hint } from '../Input/InputHint';
import { Label } from '../Input/InputLabel';
import { Stack } from '../Stack/Stack';
import { VisuallyHidden } from '../VisuallyHidden/VisuallyHidden';

/**
 * Native input element attributes picked from InputHTMLAttributes. More info on what attributes
 * are available: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#attributes
 */
type InputPasswordHTMLAttributes = Pick<
  InputHTMLAttributes<HTMLInputElement>,
  'value' /** used for controlled inputs */ | 'onChange' | 'onBlur' | 'onFocus'
>;

/** Allowed autocomplete values for InputPassword component */
type InputPasswordAutocompleteAttribute = 'current-password' | 'new-password' | 'one-time-code';

type InputPasswordProps = Omit<InputBaseProps, 'isOptional' | 'isDisabled'> & {
  /**
   * Custom `autoComplete` attribute specifically for the password input. More info on when to
   * use a certain value can be found here:
   * https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#autofilling-form-controls:-the-autocomplete-attribute
   */
  autoComplete: InputPasswordAutocompleteAttribute;
};

type Props = InputPasswordProps & InputPasswordHTMLAttributes;

export const InputPassword = forwardRef<HTMLInputElement, Props>(
  ({ error, hint, isReadOnly, label, name, ...rest }, ref) => {
    const localRef = useRef<HTMLInputElement>(null);
    const mergedRef = mergeRefs([ref, localRef]);

    const [type, setType] = useState<'password' | 'text'>('password');
    const { inputId, describedBy, errorId, hintId } = useInputIds({ error, hint });

    const { showPassword, hidePassword, isShown, isHidden } = useI18nTranslations();

    useValidationErrorEvent({ error, name: name }, localRef);

    const toggleShowPassword = () => {
      setType(prevType => (prevType === 'password' ? 'text' : 'password'));
    };

    const buttonLabel = type === 'password' ? showPassword : hidePassword;

    return (
      <Stack direction="column">
        <Stack.Item grow>
          <Label htmlFor={inputId}>
            {label}
            <div className={relativeWrapperStyle}>
              <div className={inputContainerRecipe({ isInvalid: !!error })}>
                <input
                  className={inputStyle}
                  aria-describedby={describedBy}
                  aria-invalid={error ? true : undefined}
                  aria-required
                  id={inputId}
                  name={name}
                  readOnly={isReadOnly}
                  ref={mergedRef}
                  type={type}
                  {...rest}
                />
              </div>
              <button
                className={inputButtonStyle}
                aria-label={buttonLabel}
                onClick={toggleShowPassword}
                type="button">
                <VisuallyHidden>{buttonLabel}</VisuallyHidden>
                {type === 'password' ? (
                  <VisibilityIcon color="iconSecondary" />
                ) : (
                  <VisibilityOffIcon color="iconSecondary" />
                )}
              </button>
            </div>
          </Label>
        </Stack.Item>
        <VisuallyHidden ariaLive="polite">{`${label} ${type === 'password' ? isHidden : isShown}`}</VisuallyHidden>
        {error ? <InputError id={errorId}>{error}</InputError> : null}
        {hint ? (
          <Box paddingTop="2">
            <Hint id={hintId}>{hint}</Hint>
          </Box>
        ) : null}
      </Stack>
    );
  },
);
